'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';


interface NavigationSection {
  id: string;
  label: string;
  offset: number;
}

interface StickyNavigationProps {
  isOpen?: boolean;
  onToggle?: () => void;
}

const StickyNavigation = ({ isOpen = false, onToggle }: StickyNavigationProps) => {
  const [activeSection, setActiveSection] = useState<string>('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(isOpen);
  const [isScrolled, setIsScrolled] = useState(false);

  const navigationSections: NavigationSection[] = [
    { id: 'services', label: 'Services', offset: 80 },
    { id: 'projects', label: 'Projects', offset: 80 },
    { id: 'about', label: 'About', offset: 80 },
    { id: 'contact', label: 'Contact', offset: 80 },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);

      const scrollPosition = window.scrollY + 100;
      
      for (let i = navigationSections.length - 1; i >= 0; i--) {
        const section = document.getElementById(navigationSections[i].id);
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(navigationSections[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(isOpen);
  }, [isOpen]);

  const handleSectionClick = (sectionId: string) => {
    const section = document.getElementById(sectionId);
    if (section) {
      const offset = 80;
      const elementPosition = section.offsetTop - offset;
      
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
      
      setIsMobileMenuOpen(false);
      if (onToggle) onToggle();
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    if (onToggle) onToggle();
  };

  return (
    <>
      <nav 
        className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-250 ${
          isScrolled ? 'bg-card shadow-md' : 'bg-card'
        }`}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between h-nav">
            <Link 
              href="/landing-page" 
              className="flex items-center space-x-3 transition-opacity duration-250 hover:opacity-80"
              aria-label="New Capital EPC Home"
            >
              <AppImage
                src="/assets/images/New_Capital_for_General_Contracting_Company_Profile_Overview_New_Capital_for_General_Contracting_final_hassan-1768050880430.jpeg"
                alt="New Capital for General Contracting Company Logo"
                width={48}
                height={48}
                className="flex-shrink-0 rounded-lg object-cover"
              />
              <div className="flex flex-col">
                <span className="font-headline font-bold text-lg text-primary leading-tight">
                  New Capital
                </span>
                <span className="font-body text-xs text-muted-foreground leading-tight">
                  EPC Solutions
                </span>
              </div>
            </Link>

            <div className="hidden md:flex items-center space-x-8">
              {navigationSections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => handleSectionClick(section.id)}
                  className={`font-body font-semibold text-base transition-colors duration-250 hover:text-primary relative py-2 ${
                    activeSection === section.id 
                      ? 'text-primary' :'text-foreground'
                  }`}
                  aria-current={activeSection === section.id ? 'page' : undefined}
                >
                  {section.label}
                  {activeSection === section.id && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
                  )}
                </button>
              ))}
            </div>

            <button
              onClick={toggleMobileMenu}
              className="md:hidden p-2 text-foreground hover:text-primary transition-colors duration-250"
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMobileMenuOpen}
            >
              <Icon 
                name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} 
                size={28} 
                variant="outline"
              />
            </button>
          </div>
        </div>
      </nav>

      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 z-[110] md:hidden bg-card"
          style={{ top: '60px' }}
        >
          <div className="flex flex-col p-6 space-y-6">
            {navigationSections.map((section) => (
              <button
                key={section.id}
                onClick={() => handleSectionClick(section.id)}
                className={`font-body font-semibold text-lg text-left py-3 px-4 rounded-lg transition-all duration-250 ${
                  activeSection === section.id
                    ? 'bg-primary text-primary-foreground'
                    : 'text-foreground hover:bg-muted'
                }`}
                aria-current={activeSection === section.id ? 'page' : undefined}
              >
                {section.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default StickyNavigation;